import { Stack, Text } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditorItems } from '../hooks/useEditorItems';
import { useEditorStore } from '../store/editorStore';
import { AITextItemPanel } from './editor-panels/AITextItemPanel';
import { AutoImageItemPanel } from './editor-panels/AutoImageItemPanel';
import { ImageItemPanel } from './editor-panels/ImageItemPanel';
import { TextItemPanel } from './editor-panels/TextItemPanel';
import { ThumbnailPreviewModal } from './editor-panels/ThumbnailPreviewModal';
import { VariableItemPanel } from './editor-panels/VariableItemPanel';

export function EditorPanel() {
  // Lấy các state và actions từ hook
  const {
    selectedItem,
    handleUpdate,
    handleStyleUpdate,
    handleKeywordUpdate,
    handleMoveLayer
  } = useEditorItems();
  
  // Lấy state và action từ store
  const selectedItemId = useEditorStore((state) => state.selectedItemId);
  
  // State locals
  const [cachedSelectedId, setCachedSelectedId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Ref cho input từ khóa
  const keywordInputRef = useRef<HTMLInputElement>(null);

  // Ngăn tất cả các sự kiện lan truyền
  const stopEvent = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();

    // Nếu là mousedown, cần ngăn behavior mặc định
    if (e.type === 'mousedown') {
      e.preventDefault();
    }
  }, []);
  
  // Xử lý khi focus vào input keyword
  const handleKeywordFocus = useCallback((e?: React.FocusEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    if (selectedItemId) {
      setCachedSelectedId(selectedItemId);
    }
    
    // Focus vào input
    if (keywordInputRef.current) {
      keywordInputRef.current.focus();
    }
  }, [selectedItemId]);
  
  // Xử lý khi blur khỏi input keyword
  const handleKeywordBlur = useCallback((e?: React.FocusEvent) => {
    if (e) {
      e.stopPropagation();
    }
  }, []);

  // Phòng ngừa event bubbling từ các components trong modal
  useEffect(() => {
    const stopPropagation = (e: Event) => {
      // Ngăn chặn sự kiện nổi bọt từ modal
      e.stopPropagation();
    };
    
    // Tìm tất cả các modal trong EditorPanel
    const modals = document.querySelectorAll('.mantine-Modal-root');
    modals.forEach(modal => {
      modal.addEventListener('mousedown', stopPropagation);
      modal.addEventListener('click', stopPropagation);
    });
    
    // Thêm xử lý ngăn chặn sự kiện click trên input fields
    const stopInputPropagation = (e: Event) => {
      e.stopPropagation();
    };
    
    // Tìm tất cả các input, textarea và select trong EditorPanel
    const inputs = document.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      input.addEventListener('click', stopInputPropagation);
      input.addEventListener('mousedown', stopInputPropagation);
      input.addEventListener('keydown', stopInputPropagation);
    });
    
    return () => {
      modals.forEach(modal => {
        modal.removeEventListener('mousedown', stopPropagation);
        modal.removeEventListener('click', stopPropagation);
      });
      
      inputs.forEach(input => {
        input.removeEventListener('click', stopInputPropagation);
        input.removeEventListener('mousedown', stopInputPropagation);
        input.removeEventListener('keydown', stopInputPropagation);
      });
    };
  }, [showPreview]);

  // Thêm xử lý đặc biệt cho input từ khóa
  useEffect(() => {
    if (keywordInputRef.current && (selectedItem?.type === 'auto-image' || selectedItem?.type === 'auto-image-prompt')) {
      const inputEl = keywordInputRef.current;
      
      // Thêm các event listener trực tiếp vào DOM element
      const handleClick = (e: Event) => {
        e.stopPropagation();
        inputEl.focus();
      };
      
      const handleMouseDown = (e: Event) => {
        e.stopPropagation();
      };
      
      // Thêm event listeners
      inputEl.addEventListener('click', handleClick, true);
      inputEl.addEventListener('mousedown', handleMouseDown, true);
      
      // Làm cho input có thể nhận focus và input
      inputEl.style.pointerEvents = 'auto';
      
      return () => {
        // Dọn dẹp event listeners
        inputEl.removeEventListener('click', handleClick, true);
        inputEl.removeEventListener('mousedown', handleMouseDown, true);
      };
    }
  }, [selectedItem]);

  // Thêm CSS vào document để fix pointer-events cho auto-image input
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      .auto-image-editor input[type="text"] {
        pointer-events: auto !important;
        user-select: text !important;
        -webkit-user-select: text !important;
      }
      .auto-image-editor .mantine-TextInput-wrapper {
        pointer-events: auto !important;
      }
      .auto-image-editor .mantine-TextInput-input {
        pointer-events: auto !important;
        user-select: text !important;
        -webkit-user-select: text !important;
      }
      .auto-image-editor .mantine-Textarea-input {
        pointer-events: auto !important;
        user-select: text !important;
        -webkit-user-select: text !important;
      }
    `;
    document.head.appendChild(styleEl);
    
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // Nếu không có item nào được chọn, hiển thị placeholder
  if (!selectedItem) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666', marginRight: '20px', height: '100%' }}>
        <Text>Chọn một phần tử để chỉnh sửa thuộc tính</Text>
      </div>
    );
  }

  return (
    // Ngăn chặn tất cả các sự kiện truyền ra ngoài từ panel
    <div 
      style={{ 
        height: '100%',
        backgroundColor: '#fff',
        padding: '10px',
        marginRight: '5px',
        // Đảm bảo pointer-events hoạt động đúng cho cả panel
        pointerEvents: 'auto'
      }}
      onClick={stopEvent}
      onMouseDown={stopEvent}
      className={(selectedItem?.type === 'auto-image' || selectedItem?.type === 'auto-image-prompt') ? 'auto-image-editor' : ''}
    >
      <Stack gap="md" p="md" style={{ pointerEvents: 'all' }}>
        {selectedItem.type === 'text' && (
          <TextItemPanel 
            item={selectedItem} 
            handleUpdate={handleUpdate} 
            handleStyleUpdate={handleStyleUpdate} 
          />
        )}
        
        {selectedItem.type === 'image' && (
          <ImageItemPanel 
            item={selectedItem} 
            handleStyleUpdate={handleStyleUpdate} 
          />
        )}
        
        {(selectedItem.type === 'auto-image' || selectedItem.type === 'auto-image-prompt') && (
          <AutoImageItemPanel 
            item={selectedItem} 
            keywordInputRef={keywordInputRef}
            handleKeywordUpdate={handleKeywordUpdate}
            handleKeywordFocus={handleKeywordFocus}
            handleKeywordBlur={handleKeywordBlur}
            handleStyleUpdate={handleStyleUpdate}
          />
        )}

        {selectedItem.type === 'variable' && (
          <VariableItemPanel
            item={selectedItem}
            handleUpdate={handleUpdate}
          />
        )}

        {selectedItem.type === 'ai-text' && (
          <AITextItemPanel
            item={selectedItem}
            handleUpdate={handleUpdate}
            handleStyleUpdate={handleStyleUpdate}
          />
        )}

        {/* Modal preview thumbnail */}
        <ThumbnailPreviewModal
          showPreview={showPreview}
          setShowPreview={setShowPreview}
          previewUrl={previewUrl}
        />
      </Stack>
    </div>
  );
} 